import type { PaginatedResponseType } from '~/types/api'
import api from './api'

/** El descuento se pacta en dinero ('amount') o como porcentaje ('percent'). */
export type DiscountTypeType = 'amount' | 'percent'

export interface QuoteItemType {
  id: number
  quoteId: number
  description: string
  quantity: number
  /** La columna `numeric` llega como texto desde la API: normalizar con toNumber. */
  unitPrice: number | string
  discount: number | string
  discountType: DiscountTypeType
  subtotal: number | string
  notes?: string | null
  order: number
}

export interface QuoteType {
  id: number
  companyId: string
  number: string
  customerId?: number | null
  userId?: number | null
  title: string
  description?: string | null
  status: 'draft' | 'sent' | 'approved' | 'rejected' | 'expired' | 'cancelled'
  currency: string
  /** Las columnas `numeric` llegan como texto desde la API: normalizar con toNumber. */
  subtotal: number | string
  taxRate: number | string
  taxAmount: number | string
  /** Lo pactado: pesos o porcentaje, según `discountType`. */
  discount: number | string
  discountType: DiscountTypeType
  /** El descuento ya resuelto a dinero, que es lo que resta del subtotal. */
  discountAmount: number | string
  total: number | string
  issuedAt?: string | null
  validUntil?: string | null
  sentAt?: string | null
  notes?: string | null
  terms?: string | null
  items?: QuoteItemType[]
  customer?: { id: number; name: string; document?: string } | null
  /**
   * `lastSentTo` guarda el último destinatario, para proponerlo en el siguiente
   * envío. `origin` marca de dónde salió: 'catalogo-publico' son las que pidió
   * un visitante desde la ficha pública, y nadie del equipo ha mirado todavía.
   */
  meta?: { lastSentTo?: string; origin?: string; productKey?: string } | null
  createdAt: string
  updatedAt: string
}

export interface CreateQuotePayloadType {
  customerId?: number
  title: string
  description?: string
  currency?: string
  taxRate?: number
  discount?: number
  discountType?: DiscountTypeType
  validUntil?: string
  notes?: string
  terms?: string
  items: Array<{
    description: string
    quantity?: number
    unitPrice?: number
    discount?: number
    discountType?: DiscountTypeType
    notes?: string
    order?: number
  }>
}

export interface UpdateQuotePayloadType extends Partial<CreateQuotePayloadType> {
  status?: QuoteType['status']
}

export interface SendQuotePayloadType {
  to: string
  subject?: string
  /** Cuerpo del correo en markdown. Si se omite, el backend arma uno con los datos de la propuesta. */
  message?: string
}

export interface QuoteListParamsType {
  page?: number
  limit?: number
  number?: string
  title?: string
  status?: string
  customerId?: number
  currency?: string
}

export const quoteService = {
  async list(params?: QuoteListParamsType) {
    const { data } = await api.get('/quotes', { params })
    return data.data as PaginatedResponseType<QuoteType>
  },

  async get(id: number) {
    const { data } = await api.get(`/quotes/${id}`)
    return data.data as QuoteType
  },

  async create(payload: CreateQuotePayloadType) {
    const { data } = await api.post('/quotes', payload)
    return data.data as QuoteType
  },

  async update(id: number, payload: UpdateQuotePayloadType) {
    const { data } = await api.put(`/quotes/${id}`, payload)
    return data.data as QuoteType
  },

  async delete(id: number) {
    const { data } = await api.delete(`/quotes/${id}`)
    return data.data as boolean
  },

  /** Elimina varias cotizaciones de una vez. Devuelve cuántas se borraron. */
  async bulkDelete(ids: number[]) {
    const { data } = await api.post('/quotes/bulk-delete', { ids })
    return data.data as { deleted: number }
  },

  async send(id: number, payload: SendQuotePayloadType) {
    const { data } = await api.post(`/quotes/${id}/send`, payload)
    return data.data as { messageId?: string; pdfSize?: number }
  },

  /** El PDF llega como binario: se pide con responseType blob para no corromperlo. */
  async pdf(id: number) {
    const { data } = await api.get(`/quotes/${id}/pdf`, { responseType: 'blob' })
    return data as Blob
  },

  /** Abre el PDF en una pestaña nueva; el objeto URL se libera al rato. */
  async openPdf(id: number) {
    const blob = await quoteService.pdf(id)
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank', 'noopener')
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  },

  async downloadPdf(id: number, number: string) {
    const blob = await quoteService.pdf(id)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `propuesta-${number}.pdf`
    // El enlace tiene que estar en el documento para que Firefox lo active, y
    // la URL no se puede liberar en el mismo tick o la descarga sale vacía.
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }
}
